import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  Database,
  Trash2,
  Settings,
  X,
  Loader2,
  Plus,
  CheckCircle,
  Plug,
  RefreshCw,
} from "lucide-react";
import { AvailableVariable } from "../../common/ui/types";
import { formatDragVariable } from "../../utils/variableHelper";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { CustomSelect, VariableTextInput } from "../../common/node-ui";
import postgresService from "../../services/postgres.service";

type ColumnInfo = { name: string; type: string; nullable: boolean };
type KV = { id: string; column: string; value: string };
type WhereCond = {
  id: string;
  column: string;
  operator: string;
  value: string;
};

const OPERATIONS = [
  { value: "INSERT", label: "Insert" },
  { value: "UPDATE", label: "Update" },
  { value: "UPSERT", label: "Insert or Update (Upsert)" },
  { value: "SELECT", label: "Select" },
  { value: "DELETE", label: "Delete" },
];

const WHERE_OPERATORS = [
  { value: "=", label: "= equals" },
  { value: "!=", label: "≠ not equals" },
  { value: ">", label: "> greater" },
  { value: ">=", label: "≥ greater/equal" },
  { value: "<", label: "< less" },
  { value: "<=", label: "≤ less/equal" },
  { value: "LIKE", label: "LIKE" },
  { value: "ILIKE", label: "ILIKE (case-insensitive)" },
  { value: "IN", label: "IN (comma list)" },
  { value: "IS NULL", label: "IS NULL" },
  { value: "IS NOT NULL", label: "IS NOT NULL" },
];

const rid = () => Math.random().toString(36).slice(2, 9);

export const PostgresNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes, deleteElements } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connModalOpen, setConnModalOpen] = useState(false);

  // connection
  const [host, setHost] = useState<string>(data?.connection?.host || "");
  const [port, setPort] = useState<string>(
    data?.connection?.port != null ? String(data.connection.port) : "5432",
  );
  const [database, setDatabase] = useState<string>(
    data?.connection?.database || "",
  );
  const [user, setUser] = useState<string>(data?.connection?.user || "");
  const [password, setPassword] = useState<string>(
    data?.connection?.password || "",
  );
  const [ssl, setSsl] = useState<string>(
    typeof data?.connection?.ssl === "string"
      ? data.connection.ssl
      : data?.connection?.ssl
        ? "require"
        : "disable",
  );

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);

  // operation/schema/table
  const [operation, setOperation] = useState<string>(
    data?.operation || "SELECT",
  );
  const [schema, setSchema] = useState<string>(data?.schema || "public");
  const [table, setTable] = useState<string>(data?.table || "");
  const [schemas, setSchemas] = useState<string[]>([]);
  const [tables, setTables] = useState<string[]>([]);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [refreshingDb, setRefreshingDb] = useState(false);

  // add-column form
  const [addColOpen, setAddColOpen] = useState(false);
  const [newColName, setNewColName] = useState("");
  const [newColType, setNewColType] = useState("text");
  const [newColNullable, setNewColNullable] = useState(true);
  const [addingCol, setAddingCol] = useState(false);
  const [addColErr, setAddColErr] = useState<string | null>(null);

  // params
  const [fields, setFields] = useState<KV[]>(
    Array.isArray(data?._fields)
      ? data._fields
      : [{ id: rid(), column: "", value: "" }],
  );
  const [where, setWhere] = useState<WhereCond[]>(
    Array.isArray(data?._where) ? data._where : [],
  );
  const [conflictColumnsCsv, setConflictColumnsCsv] = useState<string>(
    Array.isArray(data?.conflict_columns)
      ? data.conflict_columns.join(", ")
      : "",
  );
  const [selectColumns] = useState<string[]>(
    Array.isArray(data?.columns) ? data.columns : [],
  );
  const [limit, setLimit] = useState<string>(
    data?.limit != null ? String(data.limit) : "",
  );
  const [orderBy, setOrderBy] = useState<string>(data?.order_by || "");
  const [orderDir, setOrderDir] = useState<string>(
    data?.order_direction || "ASC",
  );

  const stop = useCallback((e: any) => e.stopPropagation(), []);
  const handleDelete = useCallback(
    () => deleteElements({ nodes: [{ id }] }),
    [deleteElements, id],
  );

  const connection = useMemo(
    () => ({
      host: host.trim(),
      port: port ? Number(port) : 5432,
      database: database.trim(),
      user: user.trim(),
      password,
      ssl,
    }),
    [host, port, database, user, password, ssl],
  );

  const connectionReady = !!(host && database && user);

  /* -------- connection actions -------- */
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await postgresService.testConnection(connection);
      const payload = r?.data || r;
      setTestResult({
        ok: !!payload?.success,
        msg: payload?.message || (payload?.success ? "Connected" : "Failed"),
      });
      if (payload?.success) loadSchemas();
    } catch (e: any) {
      setTestResult({ ok: false, msg: e?.message || "Connection failed" });
    } finally {
      setTesting(false);
    }
  };

  const loadSchemas = async () => {
    if (!connectionReady) return;
    setLoadingSchemas(true);
    try {
      const r = await postgresService.listSchemas(connection);
      if (r?.success) setSchemas(r.data || []);
    } catch {
      /* ignore */
    } finally {
      setLoadingSchemas(false);
    }
  };
  const loadTables = async () => {
    if (!connectionReady || !schema) return;
    setLoadingTables(true);
    try {
      const r = await postgresService.listTables(connection, schema);
      if (r?.success) setTables(r.data || []);
    } catch {
      /* ignore */
    } finally {
      setLoadingTables(false);
    }
  };
  const loadColumns = async () => {
    if (!connectionReady || !schema || !table) {
      setColumns([]);
      return;
    }
    try {
      const r = await postgresService.listColumns(connection, schema, table);
      if (r?.success) setColumns(r.data || []);
    } catch {
      setColumns([]);
    }
  };

  // Re-fetch schemas → tables → columns from the live database. Handy when
  // tables/columns were added on the DB after this node was first configured.
  const handleRefreshDb = async () => {
    if (!connectionReady || refreshingDb) return;
    setRefreshingDb(true);
    try {
      await loadSchemas();
      if (schema) await loadTables();
      if (table) await loadColumns();
    } finally {
      setRefreshingDb(false);
    }
  };

  const handleAddColumn = async () => {
    setAddColErr(null);
    if (!table) { setAddColErr("Pick a table first"); return; }
    if (!newColName.trim()) { setAddColErr("Column name is required"); return; }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(newColName.trim())) {
      setAddColErr("Invalid column name (letters, digits, underscore; must not start with a digit)");
      return;
    }
    setAddingCol(true);
    try {
      const r = await postgresService.addColumn(connection, schema, table, newColName.trim(), newColType, newColNullable);
      const ok = r?.success ?? r?.data?.success;
      if (ok) {
        await loadColumns();
        setNewColName("");
        setNewColType("text");
        setNewColNullable(true);
        setAddColOpen(false);
      } else {
        setAddColErr(r?.error || r?.data?.error || "Failed to add column");
      }
    } catch (e: any) {
      setAddColErr(e?.message || "Failed to add column");
    } finally {
      setAddingCol(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && connectionReady) {
      loadSchemas();
      if (schema) loadTables();
      if (table) loadColumns();
    } /* eslint-disable-next-line */
  }, [isModalOpen]);
  useEffect(() => {
    if (isModalOpen && schema) loadTables(); /* eslint-disable-next-line */
  }, [isModalOpen, schema]);
  useEffect(() => {
    if (isModalOpen && table) loadColumns(); /* eslint-disable-next-line */
  }, [isModalOpen, table]);

  // When the connection credentials modal closes (Save/Cancel) with a valid
  // connection, refresh schemas/tables/columns immediately — so the dropdowns
  // populate right after setting up the connection, without needing to close
  // and reopen the main popup.
  useEffect(() => {
    if (!connModalOpen && isModalOpen && connectionReady) {
      loadSchemas();
      if (schema) loadTables();
      if (table) loadColumns();
    } /* eslint-disable-next-line */
  }, [connModalOpen]);

  /* -------- persist -------- */
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== id) return n;
        const valuesObj: Record<string, any> = {};
        fields.forEach((f) => {
          if (f.column) valuesObj[f.column] = f.value;
        });
        const whereArr = where
          .filter((w) => w.column && w.operator)
          .map((w) => ({
            column: w.column,
            operator: w.operator,
            value: w.value,
          }));
        const inputs: any = {
          connection: {
            host: host.trim(),
            port: port ? Number(port) : 5432,
            database: database.trim(),
            user: user.trim(),
            password,
            ssl,
          },
          operation,
          schema,
          table,
          values:
            operation === "INSERT" ||
            operation === "UPDATE" ||
            operation === "UPSERT"
              ? valuesObj
              : undefined,
          where:
            operation === "UPDATE" ||
            operation === "SELECT" ||
            operation === "DELETE"
              ? whereArr
              : undefined,
          conflict_columns:
            operation === "UPSERT" && conflictColumnsCsv
              ? conflictColumnsCsv
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : undefined,
          columns:
            operation === "SELECT" && selectColumns.length
              ? selectColumns
              : undefined,
          limit:
            operation === "SELECT" && limit !== "" ? Number(limit) : undefined,
          order_by: operation === "SELECT" && orderBy ? orderBy : undefined,
          order_direction:
            operation === "SELECT" && orderBy ? orderDir : undefined,
        };
        // Keep the raw editor arrays on data so the modal restores correctly.
        return {
          ...n,
          data: {
            ...(n.data as any),
            ...inputs,
            inputs,
            _fields: fields,
            _where: where,
          },
        };
      }),
    );
  }, [
    host,
    port,
    database,
    user,
    password,
    ssl,
    operation,
    schema,
    table,
    fields,
    where,
    conflictColumnsCsv,
    selectColumns,
    limit,
    orderBy,
    orderDir,
    id,
    setNodes,
  ]);

  /* -------- variables sidebar -------- */
  const extractNodeData = useCallback((nd: any) => {
    if (!nd || typeof nd !== "object") return { inputs: {}, outputs: {} };
    const outputs = nd.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nd).forEach(([k, v]) => {
      if (k !== "outputs") inputs[k] = v;
    });
    return { inputs, outputs };
  }, []);
  const previousNodes = useMemo(() => {
    return getAllAncestorNodes(id, nodes, edges).map((sn) => {
      const { inputs, outputs } = extractNodeData(sn.data);
      return {
        nodeId: sn.id,
        nodeName: sn.data?.name || sn.id,
        nodeType: sn.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, nodes, edges, extractNodeData]);
  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(
      "text/plain",
      formatDragVariable(nodeName, variable, type),
    );
  };

  const validationStatus = useMemo(() => {
    const tempNode: Node<any> = {
      id,
      type: "POSTGRES",
      position: { x: 0, y: 0 },
      data: { ...data, connection, operation, schema, table },
    };
    return getNodeValidationStatus(tempNode, edges, nodes);
  }, [id, data, connection, operation, schema, table, edges, nodes]);

  const columnOptions = useMemo(
    () =>
      columns.map((c) => ({ value: c.name, label: c.name, sublabel: c.type })),
    [columns],
  );
  const hasOutputs = data?.outputs && Object.keys(data.outputs).length > 0;
  const execStatus = data?.executionStatus;

  // field/where editors
  const addField = () =>
    setFields((p) => [...p, { id: rid(), column: "", value: "" }]);
  const removeField = (fid: string) =>
    setFields((p) => p.filter((f) => f.id !== fid));
  const updateField = (fid: string, patch: Partial<KV>) =>
    setFields((p) => p.map((f) => (f.id === fid ? { ...f, ...patch } : f)));
  const addWhere = () =>
    setWhere((p) => [
      ...p,
      { id: rid(), column: "", operator: "=", value: "" },
    ]);
  const removeWhere = (wid: string) =>
    setWhere((p) => p.filter((w) => w.id !== wid));
  const updateWhere = (wid: string, patch: Partial<WhereCond>) =>
    setWhere((p) => p.map((w) => (w.id === wid ? { ...w, ...patch } : w)));

  const inputCls =
    "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none";
  const noValueOp = (op: string) => op === "IS NULL" || op === "IS NOT NULL";

  return (
    <div
      className={`relative group bg-indigo-50/80 backdrop-blur-xl border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : execStatus === "running"
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : execStatus === "success"
              ? "border-green-500 shadow-md shadow-green-200"
              : execStatus === "error"
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-indigo-500 shadow-md"
                  : "border-indigo-300 hover:border-indigo-400"
      }`}
    >
      {execStatus === "running" && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}
      {execStatus === "success" && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-indigo-600 border-2 border-white"
        style={{ boxShadow: "0 0 0 4px rgba(79,70,229,0.25)" }}
      />

      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg shadow-sm bg-indigo-600">
            <Database className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">
              {data?.name || "PostgreSQL"}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${connectionReady ? "bg-indigo-500" : "bg-gray-400"}`}
              />
              <p className="text-xs text-gray-600">
                {OPERATIONS.find((o) => o.value === operation)?.label ||
                  "Not Configured"}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          className="text-gray-500 hover:text-gray-700 p-1 rounded"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div
        className="p-2.5 bg-white/80 border border-indigo-200 rounded-lg text-xs shadow-inner mb-3 space-y-1"
        onClick={stop}
      >
        <div className="flex gap-1.5">
          <span className="text-gray-500">DB:</span>
          <span className="text-indigo-700 font-medium truncate">
            {database || "—"}
            {host ? ` @ ${host}` : ""}
          </span>
        </div>
        <div className="flex gap-1.5">
          <span className="text-gray-500">Target:</span>
          <span className="text-gray-700 truncate">
            {table ? `${schema}.${table}` : "—"}
          </span>
        </div>
      </div>

      <div onClick={stop}>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-indigo-700"
        >
          <Settings className="w-3.5 h-3.5" /> Configure PostgreSQL
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-indigo-600 border-2 border-white"
        style={{ boxShadow: "0 0 0 4px rgba(79,70,229,0.25)" }}
      />

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-stretch justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full h-full overflow-hidden flex flex-col"
              onClick={stop}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-600 rounded">
                    <Database className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      PostgreSQL Connector
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Connect, pick an operation, schema &amp; table, then map
                      fields
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* LEFT: variables */}
                <div className="w-[440px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* CENTER: form */}
                <div className="flex-1 min-w-[440px] overflow-y-auto bg-white p-6 space-y-5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                  {/* Connection — opens a dedicated credentials modal */}
                  <div className="space-y-2">
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Connection
                    </label>

                    {connectionReady ? (
                      <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="p-1.5 rounded-md bg-indigo-100 shrink-0">
                            <Database className="w-4 h-4 text-indigo-700" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {database} <span className="text-gray-400">@</span> {host}
                              <span className="text-gray-400">:{port || 5432}</span>
                            </p>
                            <p className="text-[11px] text-gray-500 truncate flex items-center gap-2">
                              <span>user: {user}</span>
                              <span className="text-gray-300">•</span>
                              <span>SSL: {ssl}</span>
                              {testResult && (
                                <span className={testResult.ok ? "text-green-600" : "text-red-600"}>
                                  {testResult.ok ? "✓ verified" : "✕ not verified"}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setConnModalOpen(true)}
                          className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConnModalOpen(true)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md border border-dashed border-indigo-300 bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100 transition-colors"
                      >
                        <Plug className="w-4 h-4" /> Set up Connection
                      </button>
                    )}
                  </div>

                  {/* Operation */}
                  <div className="space-y-2 border-t border-gray-100 pt-5">
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Operation
                    </label>
                    <CustomSelect
                      value={operation}
                      onChange={setOperation}
                      options={OPERATIONS}
                    />
                  </div>

                  {/* Schema + Table */}
                  <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-5">
                    <div className="col-span-2 flex items-center justify-between">
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Schema &amp; Table
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => { setAddColOpen((v) => !v); setAddColErr(null); }}
                          disabled={!table}
                          title={table ? "Add a new column to this table" : "Pick a table first"}
                          className="flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add column
                        </button>
                        <button
                          type="button"
                          onClick={handleRefreshDb}
                          disabled={!connectionReady || refreshingDb}
                          title="Refresh database (fetch latest schemas & tables)"
                          className="flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${refreshingDb ? "animate-spin" : ""}`} />
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Schema
                      </label>
                      <CustomSelect
                        value={schema}
                        placeholder={
                          connectionReady ? "public" : "Test connection first"
                        }
                        disabled={!connectionReady}
                        searchable
                        onChange={(v) => {
                          setSchema(v);
                          setTable("");
                          setColumns([]);
                        }}
                        options={(schemas.length ? schemas : ["public"]).map(
                          (s) => ({ value: s, label: s }),
                        )}
                      />
                      {loadingSchemas && (
                        <p className="text-[11px] text-gray-400 mt-1">
                          Loading schemas…
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Table <span className="text-red-500">*</span>
                      </label>
                      <CustomSelect
                        value={table}
                        placeholder={
                          schema ? "Select table…" : "Pick a schema first"
                        }
                        disabled={!schema}
                        searchable
                        onChange={(v) => {
                          setTable(v);
                        }}
                        options={tables.map((t) => ({ value: t, label: t }))}
                      />
                      {loadingTables && (
                        <p className="text-[11px] text-gray-400 mt-1">
                          Loading tables…
                        </p>
                      )}
                    </div>

                    {/* Add-column inline form */}
                    {addColOpen && table && (
                      <div className="col-span-2 rounded-md border border-indigo-200 bg-indigo-50/60 p-3 space-y-2">
                        <p className="text-[11px] font-semibold text-gray-600">
                          Add a column to <span className="text-indigo-700">{schema}.{table}</span>
                        </p>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-[11px] text-gray-600 mb-1">Column name</label>
                            <input
                              value={newColName}
                              onChange={(e) => setNewColName(e.target.value)}
                              placeholder="e.g. phone_number"
                              className={inputCls}
                            />
                          </div>
                          <div className="w-44">
                            <label className="block text-[11px] text-gray-600 mb-1">Type</label>
                            <CustomSelect
                              value={newColType}
                              searchable
                              onChange={setNewColType}
                              options={[
                                { value: "text", label: "text" },
                                { value: "varchar(255)", label: "varchar(255)" },
                                { value: "integer", label: "integer" },
                                { value: "bigint", label: "bigint" },
                                { value: "numeric", label: "numeric" },
                                { value: "boolean", label: "boolean" },
                                { value: "date", label: "date" },
                                { value: "timestamp", label: "timestamp" },
                                { value: "timestamptz", label: "timestamptz" },
                                { value: "jsonb", label: "jsonb" },
                                { value: "uuid", label: "uuid" },
                                { value: "double precision", label: "double precision" },
                              ]}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-[11px] text-gray-700">
                            <input type="checkbox" checked={newColNullable} onChange={(e) => setNewColNullable(e.target.checked)} />
                            Nullable
                          </label>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setAddColOpen(false)} className="text-[11px] px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                            <button
                              type="button"
                              onClick={handleAddColumn}
                              disabled={addingCol || !newColName.trim()}
                              className="text-[11px] px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              {addingCol ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create column
                            </button>
                          </div>
                        </div>
                        {addColErr && <p className="text-[11px] text-red-600">{addColErr}</p>}
                      </div>
                    )}
                  </div>

                  {/* Operation configuration */}
                  <div className="space-y-3 border-t border-gray-100 pt-5">
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      {OPERATIONS.find((o) => o.value === operation)?.label}{" "}
                      Configuration
                    </label>

                    {/* UPSERT conflict key */}
                    {operation === "UPSERT" && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Conflict column(s){" "}
                          <span className="text-red-500">*</span>{" "}
                          <span className="text-gray-400">
                            (comma-separated)
                          </span>
                        </label>
                        <input
                          value={conflictColumnsCsv}
                          onChange={(e) =>
                            setConflictColumnsCsv(e.target.value)
                          }
                          placeholder="employee_id"
                          className={inputCls}
                        />
                      </div>
                    )}

                    {/* Field mapping for INSERT/UPDATE/UPSERT */}
                    {(operation === "INSERT" ||
                      operation === "UPDATE" ||
                      operation === "UPSERT") && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs text-gray-600">
                            Fields (column → value)
                          </label>
                          <button
                            type="button"
                            onClick={addField}
                            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> Add field
                          </button>
                        </div>
                        <div className="space-y-2">
                          {fields.map((f) => (
                            <div key={f.id} className="flex items-center gap-2">
                              <div className="w-1/3">
                                <CustomSelect
                                  value={f.column}
                                  placeholder="column"
                                  searchable
                                  onChange={(v) =>
                                    updateField(f.id, { column: v })
                                  }
                                  options={columnOptions}
                                />
                              </div>
                              <div className="flex-1">
                                <VariableTextInput
                                  value={f.value}
                                  onChange={(val) => updateField(f.id, { value: val })}
                                  placeholder="value or drag {{Prev.outputs.x}}"
                                  className={`${inputCls} w-full font-mono`}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeField(f.id)}
                                className="text-gray-400 hover:text-red-500 p-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          {columns.length === 0 && (
                            <p className="text-[11px] text-gray-400">
                              Tip: select a table to load its columns for the
                              dropdown (you can also type a column name).
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* WHERE for UPDATE/SELECT/DELETE */}
                    {(operation === "UPDATE" ||
                      operation === "SELECT" ||
                      operation === "DELETE") && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs text-gray-600">
                            Where conditions{" "}
                            {(operation === "UPDATE" ||
                              operation === "DELETE") && (
                              <span className="text-red-500">*</span>
                            )}{" "}
                            <span className="text-gray-400">(AND)</span>
                          </label>
                          <button
                            type="button"
                            onClick={addWhere}
                            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> Add condition
                          </button>
                        </div>
                        <div className="space-y-2">
                          {where.length === 0 && (
                            <p className="text-[11px] text-gray-400">
                              No conditions.{" "}
                              {operation === "UPDATE" || operation === "DELETE"
                                ? "At least one is required."
                                : "Returns all rows."}
                            </p>
                          )}
                          {where.map((w) => (
                            <div key={w.id} className="flex items-center gap-2">
                              <div className="w-1/3">
                                <CustomSelect
                                  value={w.column}
                                  placeholder="column"
                                  searchable
                                  onChange={(v) =>
                                    updateWhere(w.id, { column: v })
                                  }
                                  options={columnOptions}
                                />
                              </div>
                              <div className="w-1/4">
                                <CustomSelect
                                  value={w.operator}
                                  onChange={(v) =>
                                    updateWhere(w.id, { operator: v })
                                  }
                                  options={WHERE_OPERATORS}
                                />
                              </div>
                              <div className="flex-1">
                                <VariableTextInput
                                  value={w.value}
                                  onChange={(val) => updateWhere(w.id, { value: val })}
                                  disabled={noValueOp(w.operator)}
                                  placeholder={
                                    noValueOp(w.operator) ? "—" : "value or drag {{var}}"
                                  }
                                  className={`${inputCls} w-full font-mono disabled:bg-gray-50`}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeWhere(w.id)}
                                className="text-gray-400 hover:text-red-500 p-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* SELECT order + limit */}
                    {operation === "SELECT" && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Order by
                          </label>
                          <CustomSelect
                            value={orderBy}
                            placeholder="(none)"
                            searchable
                            onChange={setOrderBy}
                            options={[
                              { value: "", label: "(none)" },
                              ...columnOptions,
                            ]}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Direction
                          </label>
                          <CustomSelect
                            value={orderDir}
                            onChange={setOrderDir}
                            options={[
                              { value: "ASC", label: "Ascending" },
                              { value: "DESC", label: "Descending" },
                            ]}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Limit
                          </label>
                          <input
                            type="number"
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            placeholder="100"
                            className={inputCls}
                          />
                        </div>
                      </div>
                    )}

                    {(operation === "UPDATE" || operation === "DELETE") && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        Safety: a WHERE condition is required — the node refuses
                        to {operation === "UPDATE" ? "update" : "delete"} every
                        row.
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!validationStatus.isValid}
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save Configuration
                    </button>
                  </div>
                </div>

                {/* RIGHT: output */}
                <div className="w-[440px] shrink-0 bg-gray-50 overflow-y-auto p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full" />{" "}
                    Execution Output
                  </h3>
                  {!hasOutputs ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                      <Database className="w-8 h-8 mb-2 opacity-20" />
                      <p className="text-xs">Available after execution</p>
                    </div>
                  ) : (
                    <pre className="text-[11px] bg-white border border-gray-200 rounded-lg p-3 overflow-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(data.outputs, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Nested: Connection credentials modal (n8n-style "Set up connection") */}
      {connModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4"
            onClick={() => setConnModalOpen(false)}
          >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden flex flex-col" onClick={stop}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded"><Plug className="w-5 h-5 text-indigo-600" /></div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">PostgreSQL Connection</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Enter your database credentials and test them</p>
                  </div>
                </div>
                <button onClick={() => setConnModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
              </div>

              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Host <span className="text-red-500">*</span></label>
                    <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="localhost" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Port</label>
                    <input value={port} onChange={(e) => setPort(e.target.value)} placeholder="5432" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Database <span className="text-red-500">*</span></label>
                    <input value={database} onChange={(e) => setDatabase(e.target.value)} placeholder="hrms" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Username <span className="text-red-500">*</span></label>
                    <input
                      value={user}
                      onChange={(e) => setUser(e.target.value)}
                      placeholder="postgres"
                      className={inputCls}
                      name="pg-conn-user"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputCls}
                      name="pg-conn-password"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">SSL Mode</label>
                    <CustomSelect
                      value={ssl}
                      onChange={setSsl}
                      options={[
                        { value: "disable", label: "Disable", sublabel: "No TLS" },
                        { value: "allow", label: "Allow", sublabel: "Use TLS if available" },
                        { value: "require", label: "Require", sublabel: "Force TLS" },
                      ]}
                    />
                  </div>
                </div>

                {testResult && (
                  <div className={`text-xs px-3 py-2 rounded-md border ${testResult.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                    {testResult.ok ? "✓ " : "✕ "}{testResult.msg}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={!connectionReady || testing}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50"
                >
                  {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Test Connection
                </button>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setConnModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                  <button
                    type="button"
                    disabled={!connectionReady}
                    onClick={() => setConnModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Connection
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default PostgresNode;
